/**
 * DealBadges — Timing Defense Gate, SOFT_STOP, Re-entry, External Consultant.
 *
 * Locked by:
 *   - S-CP10-4 (Timing Defense Gate) — FINAL_DECISIONS_2026-05-15.md §S-CP10-4
 *   - RULE-FORECAST-1 — RULE_FORECAST_1.md
 *   - RULE-PUSH-REENTRY — RULE_PUSH_REENTRY.md
 *   - RULE-EXT-CONSULTANT — RULE_EXT_CONSULTANT.md + UI_SPEC_EXT_CONSULTANT_BADGE.md
 *
 * No exclamation points, no emoji per locked copy policy.
 */
import {
  type DeclaredTiming,
  type ForecastCategory,
  timingColor,
  timingBandLabel,
  categoryColor,
  pipelineNeedsAttention,
} from "@/lib/forecast-rules";

const pillBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  height: 18,
  padding: "0 7px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  lineHeight: 1,
  whiteSpace: "nowrap",
  fontFamily: "Inter, system-ui, sans-serif",
};

export function TimingBadge({ timing }: { timing: DeclaredTiming }) {
  const color = timingColor(timing);
  return (
    <span
      title={`Rep-declared timing confidence: ${timingBandLabel(timing)}%`}
      style={{
        ...pillBase,
        background: `${color}14`,
        color,
        border: `1px solid ${color}33`,
      }}
      data-testid="timing-badge"
    >
      <span style={{ opacity: 0.7 }}>T</span>
      {timingBandLabel(timing)}
    </span>
  );
}

export function ForecastCategoryPill({
  category, timing,
}: { category: ForecastCategory; timing?: DeclaredTiming }) {
  const color = categoryColor(category);
  const flag = timing !== undefined && pipelineNeedsAttention(timing) && category === "Pipeline";
  return (
    <span
      title={
        flag
          ? "Pipeline + manager attention flag (timing = 50%)"
          : `Forecast category: ${category}`
      }
      style={{
        ...pillBase,
        background: `${color}14`,
        color,
        border: `1px solid ${color}33`,
      }}
      data-testid="forecast-category"
    >
      {category}{flag ? " ·" : ""}
      {flag && (
        <span aria-label="attention" style={{ color: "#92400E" }}>FLAG</span>
      )}
    </span>
  );
}

export function SoftStopBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span
      title={`${count} active SOFT_STOP${count === 1 ? "" : "s"} on this deal`}
      style={{
        ...pillBase,
        background: "#FFFBEB", // orange-50 (muted per brand palette)
        color: "#92400E",      // amber-800
        border: "1px solid #FDE68A",
      }}
      data-testid="soft-stop-badge"
    >
      SOFT_STOP × {count}
    </span>
  );
}

export function ReEntryBadge({ from = "PUSH" }: { from?: string }) {
  return (
    <span
      title={`Returned to active pipeline from ${from} — CP4/CP5/CP10 re-validation required (RULE-PUSH-REENTRY)`}
      style={{
        ...pillBase,
        background: "#FEF2F2", // red-50
        color: "#B91C1C",      // red-700 (brand)
        border: "1px solid #FECACA", // red-200
      }}
      data-testid="reentry-badge"
    >
      RE-ENTRY
    </span>
  );
}

export function ExtConsultantBadge() {
  return (
    <span
      title="External consultant — full platform access, no tenant-domain email required (RULE-EXT-CONSULTANT)"
      style={{
        ...pillBase,
        background: "#FAFAF7",
        color: "#1F1F23",
        border: "1px dashed #1F1F23",
      }}
      data-testid="ext-consultant-badge"
    >
      EXT
    </span>
  );
}

/**
 * Convenience cluster — renders Timing + SOFT_STOP + RE-ENTRY in a row,
 * collapsing absent fields. Forecast category is rendered separately as its
 * own column.
 */
export function DealBadgeRow({
  timing, softStops, reEntry,
}: {
  timing?: DeclaredTiming;
  softStops?: number;
  reEntry?: boolean;
}) {
  return (
    <div style={{ display: "inline-flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
      {timing !== undefined && <TimingBadge timing={timing} />}
      {softStops ? <SoftStopBadge count={softStops} /> : null}
      {reEntry ? <ReEntryBadge /> : null}
    </div>
  );
}
